import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';

import { ApiQuoteParametersService } from '../../data/services/api/api-quote-parameters.service';
import { QuoteParametersService } from '../../data/services/view/quote-parameters.service';
import { IQuoteParameters } from '../../data/interfaces/iquote-parameters.interface';
import { PdfQuotationApiService } from '@data/services/utils/pdf-quotation-api.service';

@Component({
    selector: 'app-quote-parameters-preview',
    templateUrl: './quote-parameters-preview.component.html',
    styleUrls: ['./quote-parameters-preview.component.scss'],
    providers: [MessageService]
})
export class QuoteParametersPreviewComponent implements OnInit {
    quoteParameters?: IQuoteParameters;
    loading = false;
    downloadingPDF = false; // Nueva variable para el loader del botón PDF
    parametersId!: string;
    
    // Variables para controlar el modal de edición
    showEditModal = false;
    editingParameters = false;

    // Variable para modal de opciones PDF
    showPdfOptionsDialog = false;

    constructor(
        private apiQuoteParametersService: ApiQuoteParametersService,
        public quoteParametersService: QuoteParametersService,
        private pdfQuotationService: PdfQuotationApiService,
        private activatedRoute: ActivatedRoute,
        private messageService: MessageService,
        private router: Router,
    ) {
        this.parametersId = this.activatedRoute.snapshot.paramMap.get('id') ?? '';
    }

    ngOnInit() {
        if (this.parametersId) {
            this.loadQuoteParameters();
        }
    }

    loadQuoteParameters() {
        this.loading = true;
        this.apiQuoteParametersService.getById(this.parametersId).subscribe({
            next: (response) => {
                if (!response.error) {
                    this.quoteParameters = response.data;
                } else {
                    console.error(response.message);
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: response.message || 'Error al cargar los parámetros de cotización'
                    });
                }
                this.loading = false;
            },
            error: (error) => {
                console.error('Error loading quote parameters:', error);
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Error al cargar los parámetros de cotización'
                });
                this.loading = false;
            }
        });
    }

    // ===== MÉTODOS PARA FORMATEAR DATOS =====

    /**
     * Obtener nombre del cliente o información provisional
     */
    getClientName(): string {
        if (this.quoteParameters?.client?.name) {
            return this.quoteParameters.client.name;
        }
        if (this.quoteParameters?.provitionalClient) {
            return this.quoteParameters.provitionalClient;
        }
        return 'Cliente no especificado';
    }

    /**
     * Verificar si hay cliente definitivo
     */
    hasDefinitiveClient(): boolean {
        return !!(this.quoteParameters?.client?.name);
    }

    /**
     * Obtener información de activos
     */
    getAssetsInfo(): string {
        if (this.quoteParameters?.assets && this.quoteParameters.assets.length > 0) {
            if (typeof this.quoteParameters.assets[0] === 'string') {
                return `${this.quoteParameters.assets.length} activo(s) seleccionado(s)`;
            } else {
                return this.quoteParameters.assets.map((asset: any) => asset.name).join(', ');
            }
        }
        if (this.quoteParameters?.provitionalAsset) {
            return this.quoteParameters.provitionalAsset;
        }
        return 'No especificado';
    }

    /**
     * Verificar si hay activos definitivos
     */
    hasDefinitiveAssets(): boolean {
        return !!(this.quoteParameters?.assets && this.quoteParameters.assets.length > 0);
    }

    /**
     * Obtener valor de activos para mostrar
     */
    getAssetsValue(): number {
        if (this.quoteParameters?.assetsValue?.total) {
            return this.quoteParameters.assetsValue.total;
        }
        if (this.quoteParameters?.provitionalValue) {
            return this.quoteParameters.provitionalValue;
        }
        return 0;
    }

    /**
     * Formatear fecha
     */
    formatDate(date: Date): string {
        if (!date) return 'No disponible';
        return new Date(date).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    /**
     * Obtener información del usuario
     */
    getUserName(): string {
        if (this.quoteParameters?.user?.name) {
            return this.quoteParameters.user.name;
        }
        if (typeof this.quoteParameters?.user === 'string') {
            return this.quoteParameters.user;
        }
        return 'Usuario no especificado';
    }

    /**
     * Obtener número de accesorios
     */
    getAccessoriesCount(): number {
        return this.quoteParameters?.accessories?.length || 0;
    }

    /**
     * Obtener accesorios de seguro
     */
    getInsuranceAccessories() {
        return this.quoteParameters?.accessories?.filter(acc => acc.isInsurance) || [];
    }

    /**
     * Obtener accesorios que no son seguro
     */
    getRegularAccessories() {
        return this.quoteParameters?.accessories?.filter(acc => !acc.isInsurance) || [];
    }

    // ===== MÉTODOS PARA CÁLCULO DE MONTOS FINANCIEROS =====

    /**
     * Calcular monto del anticipo
     */
    getAdvanceAmount(): number {
        const total = this.getAssetsValue();
        const advanceRate = this.quoteParameters?.advance || 0;
        return total * (advanceRate / 100);
    }

    /**
     * Calcular monto de la comisión de apertura
     */
    getOpeningCommissionAmount(): number {
        const total = this.getAssetsValue();
        const commissionRate = this.quoteParameters?.openingCommission || 0;
        return total * (commissionRate / 100);
    }

    // ===== MÉTODOS DE ACCIONES =====

    /**
     * Navegar de vuelta al listado
     */
    goBack() {
        this.router.navigate(['/quote']);
    }

    /**
     * Navegar a editar parámetros
     */
    editParameters() {
        this.openEditModal();
    }

    // ===== MÉTODOS PARA MANEJAR EL MODAL DE EDICIÓN =====

    /**
     * Abrir modal de edición
     */
    openEditModal() {
        if (!this.quoteParameters) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Advertencia',
                detail: 'No hay parámetros para editar'
            });
            return;
        }

        // Configurar el servicio para modo edición
        this.quoteParametersService.resetQuoteAmounts();
        this.quoteParametersService.setFormMethod('EDIT');
        this.quoteParametersService.quote = this.quoteParameters;
        this.quoteParametersService.initializeForm();
        
        // Mostrar modal
        this.showEditModal = true;
    }

    /**
     * Manejar éxito en el formulario
     */
    onFormSubmitSuccess() {
        this.showEditModal = false;
        this.editingParameters = false;
        this.loadQuoteParameters(); // Recargar datos
        this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: 'Parámetros actualizados correctamente'
        });
    }

    /**
     * Manejar cancelación del formulario
     */
    onFormCancel() {
        this.showEditModal = false;
        this.editingParameters = false;
    }

    /**
     * Cerrar modal (método adicional para consistencia)
     */
    closeEditModal() {
        this.showEditModal = false;
        this.editingParameters = false;
    }

    // ===== MÉTODOS PARA MANEJAR FORMULARIO (adaptados de QuoteListComponent) =====

    /**
     * Obtener montos calculados
     */
    getAmounts() {
        this.editingParameters = true;
        const data = { ...this.quoteParametersService.entity.value };
        data.user = this.quoteParameters?.user?._id || this.quoteParameters?.user;
        delete data.id;
        delete data.case;
        data.terms = this.quoteParametersService.termsArray.value.filter((t: any) => this.quoteParametersService.isTermAllowed(t.term));
        // F-016: solo la vista previa lleva la bandera; saveForm() no la manda (el DTO la rechaza).
        data.advancedRentsIncludeAccessories = this.quoteParametersService.advancedRentsIncludeAccessoriesForPreview();

        // Limpiar provitionalValue cuando hay activos reales para evitar que el backend lo use en lugar de los activos
        // Modo de ACTIVO: provisional → provitionalAsset/Value (sin assets); real → assets (sin provitional*).
        if (this.quoteParametersService.useProvisionalAsset) {
            delete data.assets;
        } else {
            delete data.provitionalValue;
            delete data.provitionalAsset;
        }

        if (data.accessories && data.accessories.length > 0) {
            data.accessories.forEach((accessory: any) => {
                if (accessory.insuranceDetails) {
                    if (accessory.insuranceDetails.startPeriod !== null && accessory.insuranceDetails.endPeriod !== null) {
                        accessory.insuranceDetails.startPeriod = new Date(accessory.insuranceDetails.startPeriod);
                        accessory.insuranceDetails.endPeriod = new Date(accessory.insuranceDetails.endPeriod);
                    }
                }
                if (accessory.isInsurance === false) {
                    accessory.insuranceDetails = null;
                }
            });
        } else {
            data.accessories = [];
        }

        this.apiQuoteParametersService.getAmounts(data).subscribe({
            next: (response) => {
                this.editingParameters = false;
                this.quoteParametersService.updateQuoteAmounts(response.data);
                this.messageService.add({ 
                    severity: 'info', 
                    summary: 'Montos actualizados', 
                    detail: 'Los montos se calcularon correctamente' 
                });
            },
            error: (error) => {
                console.error('Error calculating amounts:', error);
                this.editingParameters = false;
                this.messageService.add({ 
                    severity: 'error', 
                    summary: 'Error al calcular montos', 
                    detail: 'Por favor, verifica los datos ingresados' 
                });
            }
        });
    }

    /**
     * Guardar formulario
     */
    saveForm() {
        this.editingParameters = true;
        const data = { ...this.quoteParametersService.entity.value };
        data.user = this.quoteParameters?.user?._id || this.quoteParameters?.user;
        data.terms = this.quoteParametersService.termsArray.value.filter((t: any) => this.quoteParametersService.isTermAllowed(t.term));

        // Limpiar provitionalValue cuando hay activos reales
        // Modo de ACTIVO: provisional → provitionalAsset/Value (sin assets); real → assets (sin provitional*).
        if (this.quoteParametersService.useProvisionalAsset) {
            delete data.assets;
        } else {
            delete data.provitionalValue;
            delete data.provitionalAsset;
        }

        if (data.accessories && data.accessories.length > 0) {
            data.accessories.forEach((accessory: any) => {
                if (accessory.insuranceDetails) {
                    if (accessory.insuranceDetails.startPeriod !== null && accessory.insuranceDetails.endPeriod !== null) {
                        accessory.insuranceDetails.startPeriod = new Date(accessory.insuranceDetails.startPeriod);
                        accessory.insuranceDetails.endPeriod = new Date(accessory.insuranceDetails.endPeriod);
                    }
                }
                if (accessory.isInsurance === false) {
                    accessory.insuranceDetails = null;
                }
            });
        } else {
            data.accessories = [];
        }

        // Determinar si es actualización basado en si tenemos un ID
        const isUpdate = this.quoteParameters?._id;

        if (isUpdate && this.quoteParameters) {
            // Actualizar parámetros existentes
            data.case = 'UPDATE';
            this.apiQuoteParametersService.update(this.quoteParameters._id!, data).subscribe({
                next: (response) => {
                    this.editingParameters = false;
                    this.onFormSubmitSuccess();
                    this.messageService.add({ 
                        severity: 'success', 
                        summary: 'Parámetros actualizados', 
                        detail: 'Los parámetros se actualizaron correctamente' 
                    });
                },
                error: (error) => {
                    console.error('Error updating parameters:', error);
                    this.editingParameters = false;
                    this.messageService.add({ 
                        severity: 'error', 
                        summary: 'Error al actualizar', 
                        detail: 'Ocurrió un error al actualizar los parámetros' 
                    });
                }
            });
        } else {
            // Crear nuevos parámetros
            data.case = 'CREATE';
            this.apiQuoteParametersService.create(data).subscribe({
                next: (response) => {
                    this.editingParameters = false;
                    this.onFormSubmitSuccess();
                    this.messageService.add({ 
                        severity: 'success', 
                        summary: 'Parámetros creados', 
                        detail: 'Los parámetros se crearon correctamente' 
                    });
                },
                error: (error) => {
                    console.error('Error creating parameters:', error);
                    this.editingParameters = false;
                    this.messageService.add({ 
                        severity: 'error', 
                        summary: 'Error al crear', 
                        detail: 'Ocurrió un error al crear los parámetros' 
                    });
                }
            });
        }

        // Reset quote amounts
        this.quoteParametersService.updateQuoteAmounts([{
            term: 0,
            irr: 0,
            advance: 0,
            openingCommission: 0,
            advancedRents: 0,
            advancedRentsNumber: 0,
            advancedRentsAccessories: 0,
            advancedRentsIva: 0,
            advancedRentsTotal: 0,
            ratificationExpenses: 0,
            accessories: [],
            advanceSubtotal: 0,
            advanceIva: 0,
            advanceTotal: 0,
            monthlyCapital: 0,
            monthlyInterest: 0,
            monthlyAccesories: [],
            monthlySubtotal: 0,
            monthlyIva: 0,
            monthlyTotal: 0,
            residuary: 0,
            residuaryIva: 0,
            residuaryTotal: 0,
        }]);
    }

    /**
     * Calcular cotización desde parámetros
     */
    calculateQuote() {
        if (!this.parametersId) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Advertencia',
                detail: 'No se puede calcular sin parámetros válidos'
            });
            return;
        }

        this.loading = true;
        this.apiQuoteParametersService.calculateQuote(this.parametersId).subscribe({
            next: (response) => {
                if (!response.error) {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Éxito',
                        detail: 'Cotización calculada correctamente'
                    });
                    // Navegar a la vista de la cotización calculada
                    this.router.navigate(['/quote/view', response.data._id]);
                } else {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: response.message || 'Error al calcular la cotización'
                    });
                }
                this.loading = false;
            },
            error: (error) => {
                console.error('Error calculating quote:', error);
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Error al calcular la cotización'
                });
                this.loading = false;
            }
        });
    }

    /**
     * Descargar PDF de parámetros — muestra confirmación para incluir/omitir residual
     */
    downloadParametersPDF() {
        if (!this.quoteParameters) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Advertencia',
                detail: 'No hay parámetros para descargar'
            });
            return;
        }

        this.showPdfOptionsDialog = true;
    }

    /**
     * Cerrar el diálogo de opciones PDF sin generar nada
     */
    closePdfDialog() {
        this.showPdfOptionsDialog = false;
    }

    /**
     * Generar PDF con la opción de residual seleccionada
     */
    generatePdf(includeResidual: boolean) {
        this.showPdfOptionsDialog = false;
        this.executeParametersPdfGeneration(includeResidual);
    }

    /**
     * Ejecutar la generación del PDF de parámetros con la opción de residual
     */
    private async executeParametersPdfGeneration(includeResidual: boolean) {
        // Post ADR-0005: el PDF se genera centralizadamente en api-rendafin
        // (`GET /quotation-parameters/:id/pdf`). El backend invoca
        // `formulasService.createQuote()` internamente, así que ya NO
        // pre-calculamos aquí — eso eliminaba un round-trip y un cálculo
        // duplicado caro (createQuote + amortización por plazo).
        this.downloadingPDF = true;
        try {
            await this.pdfQuotationService.generateQuotationPdf(this.parametersId, { includeResidual });
            this.messageService.add({
                severity: 'success',
                summary: 'Éxito',
                detail: 'PDF generado correctamente'
            });
        } catch (error) {
            console.error('Error generating PDF:', error);
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Error al generar el PDF'
            });
        } finally {
            this.downloadingPDF = false;
        }
    }

    // ===== MÉTODOS AUXILIARES =====

    /**
     * Verificar si un valor existe y no está vacío
     */
    hasValue(value: any): boolean {
        return value !== null && value !== undefined && value !== '';
    }

    /**
     * Obtener clase CSS para el tipo de arrendamiento
     */
    getLeaseTypeClass(leaseType: string): string {
        switch (leaseType?.toLowerCase()) {
            case 'puro':
                return 'lease-type-pure';
            case 'financiero':
                return 'lease-type-financial';
            default:
                return 'lease-type-default';
        }
    }

    /**
     * Verificar si hay información suficiente para calcular
     */
    canCalculate(): boolean {
        if (!this.quoteParameters) return false;
        
        const hasClient = this.hasDefinitiveClient() || this.quoteParameters.provitionalClient;
        const hasAssets = this.hasDefinitiveAssets() || this.quoteParameters.provitionalAsset;
        const hasTerms = this.quoteParameters.terms && this.quoteParameters.terms.length > 0;
        const hasFinancialData = this.quoteParameters.advance && this.quoteParameters.interestRate;
        
        return !!(hasClient && hasAssets && hasTerms && hasFinancialData);
    }

    /**
     * Obtener texto descriptivo del estado de los parámetros
     */
    getParametersStatus(): string {
        if (!this.quoteParameters) return 'No disponible';
        
        const isComplete = this.canCalculate();
        const hasProvisionalData = !this.hasDefinitiveClient() || !this.hasDefinitiveAssets();
        
        if (isComplete && !hasProvisionalData) {
            return 'Completo y listo para calcular';
        } else if (isComplete && hasProvisionalData) {
            return 'Completo con datos provisionales';
        } else {
            return 'Incompleto - faltan datos';
        }
    }

    /**
     * Obtener severidad del tag de estado
     */
    getStatusSeverity(): string {
        if (!this.quoteParameters) return 'secondary';
        
        const isComplete = this.canCalculate();
        const hasProvisionalData = !this.hasDefinitiveClient() || !this.hasDefinitiveAssets();
        
        if (isComplete && !hasProvisionalData) {
            return 'success';
        } else if (isComplete && hasProvisionalData) {
            return 'warning';
        } else {
            return 'danger';
        }
    }

    /**
     * Obtener el número total de activos
     */
    getTotalAssetsCount(): number {
        if (this.hasDefinitiveAssets()) {
            return this.quoteParameters?.assets?.length || 0;
        }
        return this.quoteParameters?.provitionalAsset ? 1 : 0;
    }

    /**
     * Obtener lista de activos definitivos con detalles
     */
    getDefinitiveAssets(): any[] {
        if (this.quoteParameters?.assets && this.quoteParameters.assets.length > 0) {
            // Si los activos son objetos completos, los retornamos
            if (typeof this.quoteParameters.assets[0] === 'object') {
                return this.quoteParameters.assets;
            }
        }
        return [];
    }

    /**
     * Verificar si los activos tienen información detallada
     */
    hasDetailedAssets(): boolean {
        const assets = this.getDefinitiveAssets();
        return assets.length > 0 && assets[0].name && assets[0].category;
    }

    /**
     * Obtener resumen de categorías de activos
     */
    getAssetsCategorySummary(): string {
        const assets = this.getDefinitiveAssets();
        if (assets.length === 0) return '';
        
        const categories = assets.map((asset: any) => asset.category).filter(Boolean);
        const uniqueCategories = [...new Set(categories)];
        
        if (uniqueCategories.length === 1) {
            return uniqueCategories[0] as string;
        } else if (uniqueCategories.length > 1) {
            return `${uniqueCategories.length} categorías diferentes`;
        }
        return '';
    }

    /**
     * Función de tracking para optimizar el renderizado de activos
     */
    trackByAssetId(index: number, asset: any): string {
        return asset._id || index;
    }

    /**
     * Obtener clase CSS para el estado del activo
     */
    getAssetStatusClass(status: string): string {
        switch (status?.toLowerCase()) {
            case 'disponible':
                return 'status-available';
            case 'arrendado':
                return 'status-leased';
            case 'mantenimiento':
                return 'status-maintenance';
            default:
                return 'status-unknown';
        }
    }
}